// Pure helpers for emergency contacts (account page) and the public SOS and
// invitation pages.
import type { Schemas } from "@10twenty/api";
import { agoText } from "../live/liveCircle";

type EmergencyContact = Schemas["EmergencyContact"];
type SosLink = Schemas["SosLink"];

export const MAX_CONTACTS = 5;
export const CONTACT_NAME_MAX = 80;
/** How often the public SOS page refreshes while the alert is open. */
export const SOS_POLL_MS = 15_000;

export type ContactStatus = "confirmed" | "pending" | "expired";

/** Confirmed, waiting for them to accept, or their link expired (resend). */
export function contactStatus(contact: EmergencyContact, nowMs: number): ContactStatus {
  if (contact.confirmed_at) return "confirmed";
  return Date.parse(contact.invite_expires_at) > nowMs ? "pending" : "expired";
}

export function contactStatusText(contact: EmergencyContact, nowMs: number, locale?: string): string {
  switch (contactStatus(contact, nowMs)) {
    case "confirmed":
      return "Confirmed";
    case "pending":
      return `Waiting for them to accept (link sent ${new Date(contact.invited_at).toLocaleDateString(locale)})`;
    case "expired":
      return "Link expired; send a new one";
  }
}

/** Trimmed name of 1–80 characters and something shaped like an address. */
export function validContact(name: string, email: string): boolean {
  const trimmed = [...name.trim()].length;
  const address = email.trim();
  return (
    trimmed >= 1 &&
    trimmed <= CONTACT_NAME_MAX &&
    address.length <= 254 &&
    /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:".]+$/.test(address)
  );
}

/** Text for the server's emergency-contact error codes. */
export function contactErrorText(code: string | undefined): string | undefined {
  switch (code) {
    case "contact_exists":
      return "That address is already on your list.";
    case "contact_limit":
      return `You can have at most ${MAX_CONTACTS} emergency contacts.`;
    case "contact_confirmed":
      return "They have already accepted.";
    case "email_disabled":
      return "This server can't send email, so emergency contacts are unavailable.";
    case "email_failed":
      return "The email could not be sent. Check the address and try again.";
    case "rate_limited":
      return "Too many invitations today. Try again tomorrow.";
    default:
      return undefined;
  }
}

/** The headline of the public SOS page. */
export function sosHeadline(link: SosLink, locale?: string): string {
  const at = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  switch (link.state) {
    case "open":
      return `${link.name} sent an SOS at ${at(link.started_at)}`;
    case "resolved":
      return `${link.name} said they are safe at ${at(link.resolved_at ?? link.started_at)}`;
    case "expired":
      return `${link.name}'s SOS ended at ${at(link.resolved_at ?? link.started_at)}`;
  }
}

/** "Location updated 2 min ago", or a time for older fixes. */
export function locationAgeText(ts: string, nowMs: number, locale?: string): string {
  const ms = Date.parse(ts);
  const ago = agoText(nowMs, ms);
  return ago
    ? `Location updated ${ago}`
    : `Location updated at ${new Date(ms).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
}

/** A link that opens the spot in the visitor's own maps (OpenStreetMap on the web). */
export function mapsUrl(lat: number, lon: number): string {
  const y = lat.toFixed(6);
  const x = lon.toFixed(6);
  return `https://www.openstreetmap.org/?mlat=${y}&mlon=${x}#map=17/${y}/${x}`;
}
